// ─── Types ───────────────────────────────────────────────────────────────────

export type TipoDocumento =
  | 'factura_recibida'
  | 'factura_emitida'
  | 'nomina'
  | 'extracto_bancario'
  | 'contrato'
  | 'presupuesto'
  | 'albaran'
  | 'otro';

export interface ClassificationResult {
  tipo: TipoDocumento;
  confidence: number; // 0-1
}

// ─── Pattern tables ──────────────────────────────────────────────────────────

interface PatternRule {
  tipo: TipoDocumento;
  /** Weight added to the score when this pattern matches. */
  weight: number;
  pattern: RegExp;
}

const FILENAME_PATTERNS: PatternRule[] = [
  // Facturas recibidas (purchase invoices / supplier invoices)
  { tipo: 'factura_recibida', weight: 0.7, pattern: /factura[_\s-]?rec/i },
  { tipo: 'factura_recibida', weight: 0.6, pattern: /fra[_\s-]?rec/i },
  { tipo: 'factura_recibida', weight: 0.5, pattern: /compra|proveedor|supplier|purchase/i },
  { tipo: 'factura_recibida', weight: 0.4, pattern: /factura|invoice|fra\b/i },

  // Facturas emitidas (sales invoices)
  { tipo: 'factura_emitida', weight: 0.7, pattern: /factura[_\s-]?emi/i },
  { tipo: 'factura_emitida', weight: 0.6, pattern: /fra[_\s-]?emi/i },
  { tipo: 'factura_emitida', weight: 0.5, pattern: /venta|cliente|sales/i },

  // Nóminas
  { tipo: 'nomina', weight: 0.8, pattern: /n[oó]mina/i },
  { tipo: 'nomina', weight: 0.6, pattern: /payroll|payslip|salario/i },
  { tipo: 'nomina', weight: 0.5, pattern: /recibo[_\s-]?salar/i },

  // Extractos bancarios
  { tipo: 'extracto_bancario', weight: 0.8, pattern: /extracto[_\s-]?banc/i },
  { tipo: 'extracto_bancario', weight: 0.6, pattern: /bank[_\s-]?statement/i },
  { tipo: 'extracto_bancario', weight: 0.5, pattern: /movimientos[_\s-]?banc/i },
  { tipo: 'extracto_bancario', weight: 0.4, pattern: /extracto|statement/i },

  // Contratos
  { tipo: 'contrato', weight: 0.8, pattern: /contrato/i },
  { tipo: 'contrato', weight: 0.6, pattern: /contract|agreement/i },
  { tipo: 'contrato', weight: 0.5, pattern: /convenio|acuerdo/i },

  // Presupuestos
  { tipo: 'presupuesto', weight: 0.8, pattern: /presupuesto/i },
  { tipo: 'presupuesto', weight: 0.6, pattern: /budget|quote|quotation/i },
  { tipo: 'presupuesto', weight: 0.5, pattern: /estimaci[oó]n|propuesta/i },

  // Albaranes
  { tipo: 'albaran', weight: 0.8, pattern: /albar[aá]n/i },
  { tipo: 'albaran', weight: 0.6, pattern: /delivery[_\s-]?note/i },
  { tipo: 'albaran', weight: 0.5, pattern: /nota[_\s-]?entrega|remisi[oó]n/i },
];

const TEXT_PATTERNS: PatternRule[] = [
  // Factura recibida clues
  { tipo: 'factura_recibida', weight: 0.5, pattern: /base\s*imponible|iva|igic|irpf/i },
  { tipo: 'factura_recibida', weight: 0.4, pattern: /n\.?\s*factura|invoice\s*n/i },
  { tipo: 'factura_recibida', weight: 0.3, pattern: /total\s*factura|amount\s*due/i },

  // Factura emitida clues (text-only if "emitida" appears)
  { tipo: 'factura_emitida', weight: 0.6, pattern: /factura\s*emitida/i },

  // Nómina clues
  { tipo: 'nomina', weight: 0.6, pattern: /salario\s*bruto|retenci[oó]n|seguridad\s*social/i },
  { tipo: 'nomina', weight: 0.5, pattern: /n[oó]mina|devengos|deducciones/i },
  { tipo: 'nomina', weight: 0.4, pattern: /cuenta\s*cotizaci[oó]n|contingencias/i },

  // Extracto bancario clues
  { tipo: 'extracto_bancario', weight: 0.6, pattern: /saldo\s*(anterior|final)|movimientos/i },
  { tipo: 'extracto_bancario', weight: 0.4, pattern: /debe\s*haber|concepto\s*importe/i },

  // Contrato clues
  { tipo: 'contrato', weight: 0.5, pattern: /cl[aá]usula|estipulaci[oó]n|partes\s*contratantes/i },
  { tipo: 'contrato', weight: 0.4, pattern: /vigencia|resoluci[oó]n|penalizaci[oó]n/i },

  // Presupuesto clues
  { tipo: 'presupuesto', weight: 0.5, pattern: /presupuesto|validez\s*de\s*la\s*oferta/i },
  { tipo: 'presupuesto', weight: 0.4, pattern: /condiciones\s*de\s*pago.*presupuesto/i },

  // Albarán clues
  { tipo: 'albaran', weight: 0.5, pattern: /albar[aá]n|nota\s*de\s*entrega/i },
  { tipo: 'albaran', weight: 0.4, pattern: /unidades\s*servidas|bultos|entrega/i },
];

// MIME-type hints
const MIME_HINTS: Partial<Record<string, TipoDocumento>> = {
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'extracto_bancario',
  'application/vnd.ms-excel': 'extracto_bancario',
};

// ─── Classification engine ───────────────────────────────────────────────────

/**
 * Classify a document based on its filename, MIME type, and optionally
 * extracted text content.
 *
 * Returns the most likely `tipo` with a confidence score (0–1).
 */
export function classifyDocument(
  filename: string,
  mimeType?: string,
  extractedText?: string
): ClassificationResult {
  const scores: Record<TipoDocumento, number> = {
    factura_recibida: 0,
    factura_emitida: 0,
    nomina: 0,
    extracto_bancario: 0,
    contrato: 0,
    presupuesto: 0,
    albaran: 0,
    otro: 0,
  };

  // 1. Filename patterns
  for (const rule of FILENAME_PATTERNS) {
    if (rule.pattern.test(filename)) {
      scores[rule.tipo] += rule.weight;
    }
  }

  // 2. MIME-type hint (small boost)
  if (mimeType && MIME_HINTS[mimeType]) {
    scores[MIME_HINTS[mimeType]!] += 0.15;
  }

  // 3. Extracted text patterns (if provided)
  if (extractedText) {
    for (const rule of TEXT_PATTERNS) {
      if (rule.pattern.test(extractedText)) {
        scores[rule.tipo] += rule.weight;
      }
    }
  }

  // Find the winner
  let bestTipo: TipoDocumento = 'otro';
  let bestScore = 0;

  for (const [tipo, score] of Object.entries(scores) as [TipoDocumento, number][]) {
    if (score > bestScore) {
      bestScore = score;
      bestTipo = tipo;
    }
  }

  // Normalise confidence to 0–1 range (cap at 1)
  const confidence = Math.min(bestScore, 1);

  // If nothing scored above a minimum threshold, fall back to 'otro'
  if (confidence < 0.2) {
    return { tipo: 'otro', confidence: 0 };
  }

  return { tipo: bestTipo, confidence: Math.round(confidence * 100) / 100 };
}
